import { useEffect, useState, useCallback } from "react";
import { Loader2, PenLine, Upload, Trash2, Building2, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

function SigImage({ kind, version }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null;
    api.get(`/finance/signatures/${kind}/image`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [kind, version]);
  return url ? <img src={url} alt="Tanda tangan" data-testid={`sig-image-${kind}`} /> : <span className="fin-muted">Memuat…</span>;
}

function SigCard({ kind, title, desc, sig, editable, needName, onChanged }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState(sig?.signer_name || "");
  const [jab, setJab] = useState(sig?.signer_title || "");
  const [busy, setBusy] = useState(null);
  const [ver, setVer] = useState(0);
  useEffect(() => { setName(sig?.signer_name || ""); setJab(sig?.signer_title || ""); }, [sig]);

  const save = async () => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (needName) fd.append("signer_name", name);
    fd.append("signer_title", jab);
    setBusy("save");
    try { await api.post(`/finance/signatures/${kind}`, fd); toast.success("Tanda tangan disimpan"); setFile(null); setVer((v) => v + 1); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const remove = async () => {
    if (!window.confirm("Hapus tanda tangan ini?")) return;
    setBusy("del");
    try { await api.delete(`/finance/signatures/${kind}`); toast.success("Tanda tangan dihapus"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="sig-card" data-testid={`sig-card-${kind}`}>
      <strong>{title}</strong><small className="fin-muted">{desc}</small>
      <div className="sig-preview">{sig ? <SigImage kind={kind} version={ver} /> : <span className="fin-muted" data-testid={`sig-empty-${kind}`}>Belum ada tanda tangan</span>}</div>
      {sig && <small className="fin-muted">{sig.signer_name ? `${sig.signer_name}${sig.signer_title ? ` · ${sig.signer_title}` : ""} · ` : ""}diunggah {sig.uploaded_by_name} · {fmtDateTime(sig.uploaded_at)}</small>}
      {editable && <>
        <div className="sig-fields">
          {needName && <label className="rj-field">Nama penanda tangan<input className="rj-input" value={name} onChange={(e) => setName(e.target.value)} data-testid={`sig-name-${kind}`} /></label>}
          <label className="rj-field">Jabatan (opsional)<input className="rj-input" value={jab} onChange={(e) => setJab(e.target.value)} placeholder="Finance" data-testid={`sig-title-${kind}`} /></label>
        </div>
        <label className="sig-file"><Upload size={14} /> {file ? file.name : "Pilih gambar PNG/JPG (latar putih)"}
          <input type="file" accept="image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`sig-file-${kind}`} /></label>
        <div className="reminder-actions">
          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={save} disabled={!!busy || (!file && !sig)} data-testid={`sig-save-${kind}`}>
            {busy === "save" ? <Loader2 size={14} className="spin" /> : <><Save size={14} /> Simpan</>}</button>
          {sig && <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={remove} disabled={!!busy} data-testid={`sig-delete-${kind}`}><Trash2 size={14} /> Hapus</button>}
        </div>
      </>}
    </div>
  );
}

function CompanyHeader() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    api.get("/finance/company-profile", { params: { refresh } }).then((r) => { setP(r.data.data); if (refresh) toast.success("Kop invoice diperbarui dari Odoo"); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);
  if (!p) return <div className="rj-loading"><Loader2 className="spin" /> Memuat kop…</div>;
  return (
    <div className="company-head" data-testid="company-header">
      <Building2 size={18} />
      <div><strong data-testid="company-name">{p.name}</strong><small>{p.address || "Alamat belum diisi di Odoo"}</small>
        <small>{[p.phone && `Telp ${p.phone}`, p.email, p.vat && `NPWP ${p.vat}`].filter(Boolean).join(" · ")}</small>
        {p.bank_text && <small>Rekening: {p.bank_text}</small>}</div>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => load(true)} disabled={busy} data-testid="company-refresh-btn">
        {busy ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Perbarui dari Odoo</button>
    </div>
  );
}

export function SignatureSettings() {
  const { hasPerm } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/finance/signatures").then((r) => setD(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="panel reminder-panel" data-testid="signature-settings">
      <div className="panel-head"><div><h3><PenLine size={16} style={{ verticalAlign: -3 }} /> Invoice PDF: Kop & Tanda Tangan</h3>
        <p>Invoice dicetak A5 landscape (ramah printer dot-matrix). Kop otomatis dari data perusahaan di Odoo. Tanda tangan memakai milik pembuat invoice; jika belum ada, memakai tanda tangan global.</p></div></div>
      <CompanyHeader />
      {!d ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : (
        <div className="sig-grid">
          {hasPerm("invoices:create") && <SigCard kind="me" title="Tanda tangan saya" desc="Dipakai pada invoice yang Anda buat." sig={d.mine} editable onChanged={load} />}
          <SigCard kind="global" title="Tanda tangan global (cadangan)" desc={d.can_manage_global ? "Dipakai jika pembuat invoice belum punya tanda tangan." : "Hanya Administrator / Finance Manager yang dapat mengubah."}
            sig={d.global} editable={d.can_manage_global} needName onChanged={load} />
        </div>
      )}
    </div>
  );
}
